/** Sidebar / bottom-nav structure. Order is fixed by PLAN.md §6 and must not change. */
export interface NavItem {
  label: string;
  path: string;
  /** Permission string `module.resource.view` required to see the item. */
  permission: string;
}

export interface NavSection {
  id: string;
  label: string;
  icon: string;
  path: string;
  permission: string;
  items: NavItem[];
  /** Shown as a bottom tab on phones; every section is also listed in the More sheet. */
  phoneTab: boolean;
}

export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'layout-dashboard',
    path: '/dashboard',
    permission: 'dashboard.overview.view',
    phoneTab: true,
    items: [],
  },
  {
    id: 'accounting',
    label: 'Accounting',
    icon: 'calculator',
    path: '/accounting',
    permission: 'accounting.report.view',
    phoneTab: false,
    items: [
      {
        label: 'Cash Flow Statement',
        path: '/accounting/cash-flow',
        permission: 'accounting.report.view',
      },
      {
        label: 'Income Statement',
        path: '/accounting/income-statement',
        permission: 'accounting.report.view',
      },
      {
        label: 'Balance Sheet',
        path: '/accounting/balance-sheet',
        permission: 'accounting.report.view',
      },
      {
        label: 'Trial Balance',
        path: '/accounting/trial-balance',
        permission: 'accounting.report.view',
      },
      {
        label: 'General Ledger',
        path: '/accounting/general-ledger',
        permission: 'accounting.ledger.view',
      },
      {
        label: 'Journal Entries',
        path: '/accounting/journals',
        permission: 'accounting.journal.view',
      },
      {
        label: 'Chart of Accounts',
        path: '/accounting/chart-of-accounts',
        permission: 'accounting.account.view',
      },
      { label: 'Ageing Reports', path: '/accounting/ageing', permission: 'accounting.report.view' },
      {
        label: 'Fiscal Periods',
        path: '/accounting/periods',
        permission: 'accounting.period.view',
      },
      {
        label: 'VAT & WHT Reports',
        path: '/accounting/tax-reports',
        permission: 'accounting.report.view',
      },
    ],
  },
  {
    id: 'purchases',
    label: 'Purchases & Expenses',
    icon: 'shopping-cart',
    path: '/purchases',
    permission: 'purchases.order.view',
    phoneTab: true,
    items: [
      { label: 'Purchase Orders', path: '/purchases/orders', permission: 'purchases.order.view' },
      { label: 'Payout Receipts', path: '/purchases/payouts', permission: 'purchases.payout.view' },
      { label: 'Vendors', path: '/purchases/vendors', permission: 'purchases.vendor.view' },
      { label: 'Vendor Bills', path: '/purchases/bills', permission: 'purchases.bill.view' },
      { label: 'Expenses', path: '/purchases/expenses', permission: 'purchases.expense.view' },
    ],
  },
  {
    id: 'sales',
    label: 'Sales & Invoices',
    icon: 'receipt',
    path: '/sales',
    permission: 'sales.invoice.view',
    phoneTab: true,
    items: [
      { label: 'Payment Receipts', path: '/sales/receipts', permission: 'sales.receipt.view' },
      { label: 'Invoices', path: '/sales/invoices', permission: 'sales.invoice.view' },
      { label: 'Proforma Invoices', path: '/sales/proformas', permission: 'sales.proforma.view' },
      {
        label: 'Quotations & Estimates',
        path: '/sales/quotations',
        permission: 'sales.quotation.view',
      },
      { label: 'Clients & Prospects', path: '/sales/clients', permission: 'sales.client.view' },
      { label: 'Credit Notes', path: '/sales/credit-notes', permission: 'sales.creditnote.view' },
      {
        label: 'Recurring & Renewals',
        path: '/sales/recurring',
        permission: 'sales.recurring.view',
      },
      { label: 'Products & Services', path: '/sales/items', permission: 'sales.item.view' },
    ],
  },
  {
    id: 'crm',
    label: 'Sales CRM & Leads',
    icon: 'target',
    path: '/crm',
    permission: 'crm.lead.view',
    phoneTab: true,
    items: [
      { label: 'All Leads', path: '/crm/leads', permission: 'crm.lead.view' },
      { label: 'Pipelines', path: '/crm/pipelines', permission: 'crm.pipeline.view' },
      { label: 'Tasks & Follow-ups', path: '/crm/tasks', permission: 'crm.lead.view' },
      { label: 'CRM Reports', path: '/crm/reports', permission: 'crm.lead.view' },
    ],
  },
  {
    id: 'team',
    label: 'Manage Team',
    icon: 'users',
    path: '/team',
    permission: 'team.user.view',
    phoneTab: false,
    items: [
      { label: 'Manage Users', path: '/team/users', permission: 'team.user.view' },
      { label: 'Team Roles', path: '/team/roles', permission: 'team.role.view' },
    ],
  },
  {
    id: 'banking',
    label: 'Banking & Payments',
    icon: 'landmark',
    path: '/banking',
    permission: 'banking.account.view',
    phoneTab: false,
    items: [
      {
        label: 'Bank Accounts',
        path: '/banking/bank-accounts',
        permission: 'banking.account.view',
      },
      {
        label: 'Payment Accounts',
        path: '/banking/payment-accounts',
        permission: 'banking.account.view',
      },
      { label: 'Transfers', path: '/banking/transfers', permission: 'banking.transfer.view' },
      {
        label: 'Bank Reconciliation',
        path: '/banking/reconciliation',
        permission: 'banking.reconciliation.view',
      },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'settings',
    path: '/settings',
    permission: 'settings.company.view',
    phoneTab: false,
    items: [
      { label: 'Company Profile', path: '/settings/company', permission: 'settings.company.view' },
      { label: 'Tax & Currencies', path: '/settings/tax', permission: 'settings.tax.view' },
      { label: 'Numbering', path: '/settings/numbering', permission: 'settings.numbering.view' },
      {
        label: 'Templates & Messages',
        path: '/settings/templates',
        permission: 'settings.template.view',
      },
      {
        label: 'Integrations',
        path: '/settings/integrations',
        permission: 'settings.company.view',
      },
      {
        label: 'Reminder Rules',
        path: '/settings/reminders',
        permission: 'settings.reminder.view',
      },
      { label: 'Audit Log', path: '/settings/audit-log', permission: 'settings.audit.view' },
    ],
  },
];

export const NAV_SECTION_LABELS = NAV_SECTIONS.map((s) => s.label);

/** Wildcard permission held by the Owner role. */
export const ALL_PERMISSIONS = '*';

export function hasPermission(granted: readonly string[], required: string): boolean {
  return granted.includes(ALL_PERMISSIONS) || granted.includes(required);
}

export function visibleSections(granted: readonly string[]): NavSection[] {
  return NAV_SECTIONS.filter((s) => hasPermission(granted, s.permission)).map((s) => ({
    ...s,
    items: s.items.filter((i) => hasPermission(granted, i.permission)),
  }));
}
